import React, { useRef, useCallback, useState } from "react";
import { useReactToPrint } from "react-to-print";
import config from "@plone/volto/registry";
import {
  resolveTemplate,
  headerTemplate,
  footerTemplate,
} from "./print.config";
import "./PrintReport.css";

const isBrowser = typeof document !== "undefined";

const PrinterIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
    className="print-report-spinner"
  >
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const PrintReportView = () => {
  const printAreaRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const blockConfig =
    config.blocks.blocksConfig?.printReport?.printConfig || {};
  const { printContainer = "#main-content", excludeBlocks = ["printReport"] } =
    blockConfig;

  // Force the print iframe to A4 width to prevent narrow column reflow,
  // then add block exclusions
  const exclusionStyles = [
    "html, body { width: 210mm; }",
    excludeBlocks.length
      ? `@media print { ${excludeBlocks
          .map((id) => `[data-block-type="${id}"]`)
          .join(", ")} { display: none !important; } }`
      : "",
  ].join("\n");

  // react-to-print v3 requires onBeforePrint to return a Promise
  const handleBeforePrint = useCallback(() => {
    return new Promise((resolve) => {
      if (isBrowser) {
        const container = document.querySelector(printContainer);
        if (container) {
          const header = document.createElement("div");
          header.className = "print-report-header print-injected";
          header.innerHTML = resolveTemplate(headerTemplate);
          container.prepend(header);

          const footer = document.createElement("div");
          footer.className = "print-report-footer print-injected";
          footer.innerHTML = resolveTemplate(footerTemplate);
          container.append(footer);
        }
      }
      resolve();
    });
  }, [printContainer]);

  // Remove injected header/footer and reset spinner after print dialog closes
  const handleAfterPrint = useCallback(() => {
    if (!isBrowser) return;
    document.querySelectorAll(".print-injected").forEach((el) => el.remove());
    setIsPrinting(false);
  }, []);

  // Point react-to-print at the printContainer
  const containerRef = useCallback(
    (node) => {
      if (!node || !isBrowser) return;
      printAreaRef.current = document.querySelector(printContainer) || node;
    },
    [printContainer],
  );

  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: isBrowser ? document.title : "Report",
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
    pageStyle: exclusionStyles,
  });

  const handleClick = useCallback(() => {
    if (isPrinting) return;
    setIsPrinting(true);
    handlePrint();
  }, [isPrinting, handlePrint]);

  return (
    <div className="print-report-block" ref={containerRef}>
      <div>
        <button
          className="print-report-block__button"
          onClick={handleClick}
          disabled={isPrinting}
          aria-label={isPrinting ? "Preparing print…" : "Print this report"}
          type="button"
        >
          <span className="print-report-block__button-icon">
            {isPrinting ? <SpinnerIcon /> : <PrinterIcon />}
          </span>
          <span className="print-report-block__button-text">
            {isPrinting ? "Preparing…" : "Print Report"}
          </span>
        </button>
      </div>
    </div>
  );
};

export default PrintReportView;
